import { createFileRoute, Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useModelAuditLogs } from '@/api/audit-logs'
import { useModel } from '@/api/models'
import { useWorkspace } from '@/api/workspace'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { VersionList } from '@/components/model-versions/version-list'
import { SlotList } from '@/components/models/slot-list'
import { RecordHistory } from '@/components/record-history/record-history'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

// Model detail (UI-SCREENS P-05, #100): the color slots, archived ones included, and the
// versions section (#101) with *Upload version* for the Owner. The WIP of this Model comes with
// 05. Otherwise read only; the Owner edits the Model in the P-04 modal on P-03. The Owner also sees
// the History section, with the color slots' archive entries (#105).
export const Route = createFileRoute('/w/$slug/products/$productId/models/$modelId/')({
  component: ModelDetail,
  staticData: { breadcrumb: 'models.detail.title' },
})

function ModelDetail() {
  const { t } = useTranslation()
  const { slug, productId, modelId } = Route.useParams()
  const model = useModel(slug, Number(modelId))
  const isOwner = useWorkspace(slug).data?.role === 'owner'
  const history = useModelAuditLogs(slug, Number(modelId), { enabled: isOwner })

  if (model.isPending) return <LoadingSkeleton variant="page" />
  if (model.isError || !model.data || String(model.data.product_id) !== productId) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {t('errors.generic')}
      </p>
    )
  }

  return (
    <section className="flex max-w-3xl flex-col gap-6">
      <Link
        to="/w/$slug/products/$productId"
        params={{ slug, productId }}
        className="text-sm underline-offset-4 hover:underline"
      >
        {t('models.back')}
      </Link>
      <div className="flex flex-col gap-2">
        <h1 className="font-heading text-xl font-semibold">{model.data.name}</h1>
        {model.data.archived_at ? (
          <div>
            <Badge variant="outline">{t('models.archived')}</Badge>
          </div>
        ) : null}
      </div>
      <section className="flex flex-col gap-3">
        <h2 className="font-medium">{t('models.detail.slots')}</h2>
        <SlotList slots={model.data.slots} />
      </section>
      <section className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-medium">{t('models.detail.versions')}</h2>
          {model.data.can.add_version ? (
            <Button asChild className="h-11">
              <Link
                to="/w/$slug/products/$productId/models/$modelId/versions/new"
                params={{ slug, productId, modelId }}
              >
                {t('modelVersions.upload')}
              </Link>
            </Button>
          ) : null}
        </div>
        <VersionList slug={slug} productId={productId} modelId={model.data.id} />
      </section>
      {isOwner ? <RecordHistory entries={history.data} isPending={history.isPending} isError={history.isError} /> : null}
    </section>
  )
}
